import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Drawer, Modal, Popconfirm, Spin, message, Space, Select, Input } from 'antd';
import VariablesGuide from './components/VariablesGuide';
import { useMyContext } from 'Context/MyContextProvider';
import useFlowEditor, { getOutgoingEvent, TYPES_WITH_OWN_ROUTING } from './hooks/useFlowEditor';
import useFlowVars from './hooks/useFlowVars';
import FlowEditorFooter from './components/FlowEditorFooter';
import FlowStructureTree from './components/FlowStructureTree';
import WorkflowListView from './components/WorkflowListView';
import { buildWorkflowTree } from './hooks/useWorkflowTree';
import FlowCanvas from './components/FlowCanvas';
import NodeConfigDrawer from './components/NodeConfigDrawer';
import AddNodeDrawer from './components/AddNodeDrawer';
import PublishModal from './components/PublishModal';
import { CopyOutlined, CheckOutlined, PlusOutlined, CodeSandboxOutlined, SendOutlined, SearchOutlined, UpOutlined, DownOutlined, UploadOutlined, StopOutlined } from '@ant-design/icons';
import {
  publishFlow,
  unpublishFlow,
  duplicateFlow,
  deleteFlow,
} from 'services/chatbotFlowService';
import { routesConfigToList, applyRouteEdges, intentsToRoutes } from './utils/routeByVar';
import { listRowsConfigToList } from './utils/flowListPresetRows';
import './styles/flowBuilder.css';

const FlowJsonView = ({ definition }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(JSON.stringify(definition, null, 2));
    setCopied(true);
    message.success('Copied JSON to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <Button
          type={copied ? 'default' : 'primary'}
          icon={copied ? <CheckOutlined /> : <CopyOutlined />}
          onClick={handleCopy}
          style={{
            borderColor: copied ? '#52c41a' : undefined,
            color: copied ? '#52c41a' : undefined,
            transition: 'all 0.3s ease',
          }}
        >
          {copied ? 'Copied' : 'Copy JSON'}
        </Button>
      </div>
      <pre
        style={{
          maxHeight: 400,
          overflow: 'auto',
          fontSize: 12,
          background: '#f9f9f9',
          padding: '16px',
          borderRadius: '8px',
          border: '1px solid #e8e8e8',
          fontFamily: 'SFMono-Regular, Consolas, Monaco, monospace',
          lineHeight: '1.5',
          boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.02)',
        }}
      >
        {JSON.stringify(definition, null, 2)}
      </pre>
    </div>
  );
};

export const FlowEditorContext = React.createContext(null);

const FlowEditor = () => {
  const { groupId, name, flowId } = useParams();
  const navigate = useNavigate();
  const { Permisson } = useMyContext();

  const [viewMode, setViewMode] = useState('list');
  const [addNodeOpen, setAddNodeOpen] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [varsOpen, setVarsOpen] = useState(false);
  const [publishLoading, setPublishLoading] = useState(false);
  const [siderWidth, setSiderWidth] = useState(240);
  const [showDraftBanner, setShowDraftBanner] = useState(true);

  const isResizing = useRef(false);
  const startX = useRef(0);
  const startWidth = useRef(240);

  // Lock body scroll while FlowEditor is mounted
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const handleResizerMouseDown = useCallback((e) => {
    isResizing.current = true;
    startX.current = e.clientX;
    startWidth.current = siderWidth;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    const onMouseMove = (ev) => {
      if (!isResizing.current) return;
      const delta = ev.clientX - startX.current;
      const newWidth = Math.min(500, Math.max(160, startWidth.current + delta));
      setSiderWidth(newWidth);
    };

    const onMouseUp = () => {
      isResizing.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }, [siderWidth]);

  const editor = useFlowEditor(flowId);
  const flowVars = useFlowVars(editor.definition);

  useEffect(() => {
    if (editor.hasRestoredDraft) {
      setShowDraftBanner(true);
    }
  }, [editor.hasRestoredDraft, flowId]);

  // Search state (lifted here so the search input can live in the top bar)
  const [searchQuery, setSearchQuery] = useState('');
  const [searchIndex, setSearchIndex] = useState(0);

  const searchTree = useMemo(() => buildWorkflowTree(editor.definition), [editor.definition]);

  const searchMatches = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const q = searchQuery.toLowerCase();
    const results = [];
    const scan = (items) => {
      (items || []).forEach((item) => {
        if (item.branchLabel && item.nodes) { scan(item.nodes); return; }
        if (item.isBackRef) return;
        const title = (item.meta?.listLabel?.(item.node?.config) || item.meta?.label || '').toLowerCase();
        const subtitle = (item.meta?.subtitle || '').toLowerCase();
        const nodeType = (item.node?.type || '').toLowerCase();
        const configText = item.node?.config ? JSON.stringify(item.node.config).toLowerCase() : '';
        if (title.includes(q) || subtitle.includes(q) || nodeType.includes(q) || configText.includes(q)) {
          results.push(item.node?.id);
        }
        if (item.children?.length) {
          item.children.forEach((child) => {
            if (child.branchLabel) scan([child]);
            else scan(item.children);
          });
        }
      });
    };
    scan(searchTree);
    return results;
  }, [searchQuery, searchTree]);

  const handleSearchChange = (e) => { setSearchQuery(e.target.value); setSearchIndex(0); };
  const handleNextMatch = () => { if (searchMatches.length > 0) setSearchIndex((p) => (p + 1) % searchMatches.length); };
  const handlePrevMatch = () => { if (searchMatches.length > 0) setSearchIndex((p) => (p - 1 + searchMatches.length) % searchMatches.length); };

  React.useEffect(() => {
    if (!Permisson?.includes('View Chatbot')) navigate('404');
  }, [Permisson, navigate]);

  const backToList = () => navigate(`/chatbot-flows/${groupId}/${name}`);

  const handlePublish = async () => {
    const v = await editor.runValidate();
    if (!v.valid) {
      setPublishOpen(true);
      return;
    }
    setPublishOpen(true);
  };

  const confirmPublish = async (note) => {
    setPublishLoading(true);
    try {
      await editor.saveDraft(true);
      await publishFlow(flowId, note);
      message.success('Flow published');
      setPublishOpen(false);
      editor.loadFlow();
    } catch (e) {
      message.error(e?.response?.data?.message || 'Publish failed');
    } finally {
      setPublishLoading(false);
    }
  };

  const handleUnpublish = async () => {
    setPublishLoading(true);
    try {
      await unpublishFlow(flowId);
      message.success('Flow unpublished — now in Draft mode');
      editor.loadFlow();
    } catch (e) {
      message.error(e?.response?.data?.message || 'Unpublish failed');
    } finally {
      setPublishLoading(false);
    }
  };

  // State to track which node triggered "Insert step below"
  const [insertAfterNodeId, setInsertAfterNodeId] = useState(null);

  const handleAddNodeType = (type) => {
    const nodes = editor.definition.nodes || [];
    // If insertAfterNodeId is set, we're inserting below a specific node
    const targetId = insertAfterNodeId || editor.selectedNodeId;
    const afterNode = targetId
      ? nodes.find((n) => n.id === targetId)
      : nodes[nodes.length - 1];

    // For branching nodes, add unattached — user wires via the node's own config
    if (afterNode && TYPES_WITH_OWN_ROUTING.has(afterNode.type)) {
      editor.addNode(type); // no afterNodeId → unattached
    } else {
      const event = afterNode ? getOutgoingEvent(afterNode.type) : 'completed';
      editor.addNode(type, afterNode?.id, event);
    }
    setAddNodeOpen(false);
    setInsertAfterNodeId(null);
  };

  const handleInsertAfter = (nodeId) => {
    setInsertAfterNodeId(nodeId);
    setAddNodeOpen(true);
  };

  const handleExportJson = () => {
    Modal.info({
      title: 'Flow definition JSON',
      width: 720,
      maskClosable: true,
      content: <FlowJsonView definition={editor.definition} />,
    });
  };

  const handleImportJson = () => {
    let draft = '';
    let parseError = '';

    const ImportJsonContent = () => {
      const [text, setText] = React.useState('');
      const [error, setError] = React.useState('');

      const validate = (val) => {
        try {
          const parsed = JSON.parse(val);
          if (!parsed.nodes || !Array.isArray(parsed.nodes)) {
            setError('Invalid flow JSON — must contain a "nodes" array.');
            return null;
          }
          setError('');
          return parsed;
        } catch (e) {
          setError('Invalid JSON: ' + e.message);
          return null;
        }
      };

      return (
        <div style={{ marginTop: 12 }}>
          <div style={{ marginBottom: 8, color: '#595959', fontSize: 13 }}>
            Paste your flow JSON below. The current flow will be replaced.
          </div>
          <textarea
            autoFocus
            rows={14}
            style={{
              width: '100%',
              fontFamily: 'SFMono-Regular, Consolas, Monaco, monospace',
              fontSize: 12,
              padding: 10,
              border: error ? '1px solid #ff4d4f' : '1px solid #d9d9d9',
              borderRadius: 6,
              resize: 'vertical',
              outline: 'none',
              lineHeight: 1.5,
            }}
            placeholder={'Paste JSON here...'}
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              const parsed = validate(e.target.value);
              draft = parsed;
              parseError = parsed ? '' : 'error';
            }}
          />
          {error && (
            <div style={{ color: '#ff4d4f', fontSize: 12, marginTop: 4 }}>{error}</div>
          )}
        </div>
      );
    };

    const modal = Modal.confirm({
      title: 'Import Flow JSON',
      width: 720,
      icon: <UploadOutlined />,
      maskClosable: true,
      content: <ImportJsonContent />,
      okText: 'Import',
      cancelText: 'Cancel',
      onOk: () => {
        if (!draft || parseError) {
          message.error('Please paste valid flow JSON first.');
          return Promise.reject();
        }
        editor.updateDefinition(() => ({ ...draft }));
        message.success('Flow imported. Review and save when ready.');
        modal.destroy();
      },
    });
  };

  const handleDuplicate = async () => {
    try {
      const res = await duplicateFlow(flowId);
      const copy = res?.data || res;
      message.success('Duplicated');
      navigate(`/chatbot-flows/${groupId}/${name}/editor/${copy.id}`);
    } catch {
      message.error('Duplicate failed');
    }
  };

  const handleDelete = () => {
    Modal.confirm({
      title: 'Delete this flow?',
      onOk: async () => {
        try {
          await deleteFlow(flowId);
          message.success('Deleted');
          backToList();
        } catch {
          message.error('Cannot delete active published flow');
        }
      },
    });
  };

  const syncEdgeFromConfig = useCallback(
    (nodeId, type, config) => {
      if (type === 'logic.condition' || type === 'integration.api') {
        editor.updateDefinition((prev) => {
          let edges = prev.edges || [];
          // Always re-sync both yes and no edges cleanly
          edges = edges.filter(
            (e) => !(e.from === nodeId && (e.event === 'yes' || e.event === 'no'))
          );
          if (config.on_success) {
            edges = [...edges, { from: nodeId, to: config.on_success, event: 'yes' }];
          }
          if (config.on_failure) {
            edges = [...edges, { from: nodeId, to: config.on_failure, event: 'no' }];
          }
          return { ...prev, edges };
        });
      } else if (type === 'message.template' || type === 'message.interactive_buttons') {
        const buttons = config.buttons || [];
        const nodeCapture = (config.capture_var || '').trim();
        editor.updateDefinition((prev) => {
          // Collect the set of button target node IDs
          const buttonTargets = new Set(
            buttons.map((b) => b.target_node_id).filter(Boolean)
          );

          let edges = (prev.edges || []).filter((e) => {
            // Remove stale button_reply edges from this node
            if (e.from === nodeId && e.event === 'button_reply') return false;
            // Remove spurious 'completed' edges that chain button-target siblings
            // together (artifacts left by sequential addNode calls)
            if (
              e.event === 'completed' &&
              buttonTargets.has(e.from) &&
              buttonTargets.has(e.to)
            ) {
              return false;
            }
            return true;
          });

          buttons.forEach((btn) => {
            if (btn.target_node_id) {
              const captureVar = (btn.capture_var || nodeCapture || '').trim();
              const edge = {
                from: nodeId,
                to: btn.target_node_id,
                event: 'button_reply',
                match: { payload: btn.id },
              };
              if (captureVar) {
                edge.capture = { var: captureVar };
              }
              edges.push(edge);
            }
          });
          return { ...prev, edges };
        });
      } else if (type === 'message.list_preset') {
        const rows = listRowsConfigToList(config.rows);
        const nodeCapture = (config.capture_var || '').trim();
        editor.updateDefinition((prev) => {
          const rowTargets = new Set(
            rows.map((r) => r.target_node_id).filter(Boolean)
          );

          let edges = (prev.edges || []).filter((e) => {
            if (e.from === nodeId && e.event === 'button_reply') return false;
            if (
              e.event === 'completed' &&
              rowTargets.has(e.from) &&
              rowTargets.has(e.to)
            ) {
              return false;
            }
            return true;
          });

          rows.forEach((row) => {
            if (!row.target_node_id) return;
            const captureVar = nodeCapture;
            const edge = {
              from: nodeId,
              to: row.target_node_id,
              event: 'button_reply',
              match: {
                payload: row.label || row.id,
                ...(row.id ? { id: row.id } : {}),
              },
            };
            if (captureVar) {
              edge.capture = { var: captureVar };
            }
            edges.push(edge);
          });

          return { ...prev, edges };
        });
      } else if (type === 'message.list_dynamic') {
        const rows = config.custom_rows || [];
        const onSelectNodeId = config.on_select_node_id || '';
        editor.updateDefinition((prev) => {
          const rowTargets = new Set(
            rows.map((r) => r.target_node_id).filter(Boolean)
          );

          let edges = (prev.edges || []).filter((e) => {
            if (e.from === nodeId && e.event === 'button_reply') return false;
            if (e.from === nodeId && e.event === 'user_input') return false;
            if (
              e.event === 'completed' &&
              rowTargets.has(e.from) &&
              rowTargets.has(e.to)
            ) {
              return false;
            }
            return true;
          });

          rows.forEach((row) => {
            if (!row.target_node_id) return;
            const captureVar = (row.capture_var || '').trim();
            const edge = {
              from: nodeId,
              to: row.target_node_id,
              event: 'button_reply',
              match: {
                payload: row.label || row.id,
                ...(row.id ? { id: row.id } : {}),
              },
            };
            if (captureVar) {
              edge.capture = { var: captureVar };
            }
            edges.push(edge);
          });

          if (onSelectNodeId) {
            edges.push({
              from: nodeId,
              to: onSelectNodeId,
              event: 'user_input',
            });
          }

          return { ...prev, edges };
        });
      } else if (type === 'logic.route_by_var' || type === 'ai.classify') {
        const routes =
          type === 'ai.classify'
            ? intentsToRoutes(config.intents)
            : routesConfigToList(config.routes);

        editor.updateDefinition((prev) => applyRouteEdges(prev, nodeId, routes));
      }

      // ── Generic "Next Step" handler (for nodes using _next_step) ──────────
      if (config._next_step !== undefined && !TYPES_WITH_OWN_ROUTING.has(type)) {
        const event = getOutgoingEvent(type);
        if (config._next_step) {
          // connectNodes already handles splice automatically
          editor.connectNodes(nodeId, config._next_step, event);
        } else {
          // User cleared the next step — remove the outgoing edge
          editor.updateDefinition((prev) => ({
            ...prev,
            edges: (prev.edges || []).filter(
              (e) => !(e.from === nodeId && e.event === event)
            ),
          }));
        }
      }
    },
    [editor]
  );

  /**
   * Wraps updateNodeConfig so that when `_next_step` changes (including being
   * cleared) the outgoing edge is synced immediately — not just on Save.
   * This makes the NodeSelect visually reset as soon as ✕ is clicked, because
   * `currentNextStep` in NodeConfigDrawer is derived from `definition.edges`.
   */
  const handleNodeConfigChange = useCallback(
    (nodeId, config) => {
      editor.updateNodeConfig(nodeId, config);

      if ('_next_step' in config && !TYPES_WITH_OWN_ROUTING.has(editor.selectedNode?.type)) {
        const event = getOutgoingEvent(editor.selectedNode?.type);
        if (config._next_step) {
          editor.connectNodes(nodeId, config._next_step, event);
        } else {
          editor.updateDefinition((prev) => ({
            ...prev,
            edges: (prev.edges || []).filter(
              (e) => !(e.from === nodeId && e.event === event)
            ),
          }));
        }
      }

      // Branching nodes (integration.api / logic.condition) route via yes/no
      // edges. Sync those edges live on change so the persisted edges can never
      // diverge from the visible on_success/on_failure config — previously they
      // were only written on the explicit "Save node" click, so closing the
      // drawer (✕) left the config set but the edge missing.
      const selType = editor.selectedNode?.type;
      if (selType === 'integration.api' || selType === 'logic.condition') {
        const syncBranch = (key, event) => {
          if (!(key in config)) return;
          const target = config[key];
          editor.updateDefinition((prev) => {
            let edges = (prev.edges || []).filter(
              (e) => !(e.from === nodeId && e.event === event)
            );
            if (target) {
              edges = [...edges, { from: nodeId, to: target, event }];
            }
            return { ...prev, edges };
          });
        };
        syncBranch('on_success', 'yes');
        syncBranch('on_failure', 'no');
      }
    },
    [editor]
  );

  const onSaveNode = () => {
    if (editor.selectedNode) {
      syncEdgeFromConfig(
        editor.selectedNode.id,
        editor.selectedNode.type,
        editor.selectedNode.config
      );
    }
    editor.setDrawerOpen(false);
  };

  if (editor.loading) {
    return (
      <div style={{ padding: 80, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <FlowEditorContext.Provider value={editor}>
      <div className="flow-builder-editor">
        <div className="flow-builder-editor__body">
          <FlowStructureTree
            definition={editor.definition}
            selectedNodeId={editor.selectedNodeId}
            onSelectNode={editor.selectNode}
            onAddNode={() => setAddNodeOpen(true)}
            style={{ width: siderWidth, minWidth: siderWidth, maxWidth: siderWidth }}
            flowName={editor.flow?.name}
            status={editor.flow?.status}
            onBack={backToList}
            onRename={editor.renameFlow}
            onDuplicate={handleDuplicate}
            onDelete={handleDelete}
            onExportJson={handleExportJson}
            onImportJson={handleImportJson}
          />
          <div
            className="flow-builder-resizer"
            onMouseDown={handleResizerMouseDown}
            title="Drag to resize"
          />

          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0, overflow: 'hidden', background: '#fff' }}>
            {editor.hasRestoredDraft && showDraftBanner && (
              <div className="flow-builder-draft-alert">
                <span className="flow-builder-draft-alert__text">
                  ⚠️ <strong>Unsaved progress restored:</strong> We loaded the changes you were working on before the page refreshed.
                </span>
                <div className="flow-builder-draft-alert__actions">
                  <Button
                    type="link"
                    size="small"
                    onClick={() => setShowDraftBanner(false)}
                    style={{ color: '#8c8c8c' }}
                  >
                    Keep Draft
                  </Button>
                  <Popconfirm
                    title="Discard unsaved changes?"
                    description="This will clear your draft and load the original definition from the server."
                    onConfirm={editor.discardDraft}
                    okText="Discard"
                    okButtonProps={{ danger: true }}
                    cancelText="Cancel"
                  >
                    <Button
                      type="primary"
                      danger
                      size="small"
                    >
                      Discard Draft
                    </Button>
                  </Popconfirm>
                </div>
              </div>
            )}
            {/* Unified Shared Header */}
            <div className="flow-builder-workflow-header">
              <Space style={{ flex: 1 }}>
                {viewMode === 'list' && (
                  <Input
                    placeholder="Search steps..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    allowClear
                    size="small"
                    prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                    suffix={
                      searchMatches.length > 0 ? (
                        <Space size={2}>
                          <span style={{ fontSize: 11, color: '#8c8c8c', whiteSpace: 'nowrap' }}>
                            {searchIndex + 1}/{searchMatches.length}
                          </span>
                          <Button size="small" type="text" icon={<UpOutlined />} onClick={handlePrevMatch} style={{ padding: '0 2px', height: 18 }} />
                          <Button size="small" type="text" icon={<DownOutlined />} onClick={handleNextMatch} style={{ padding: '0 2px', height: 18 }} />
                        </Space>
                      ) : null
                    }
                    style={{ width: 220 }}
                  />
                )}
              </Space>
              <Space>
                <Select
                  value={viewMode}
                  onChange={(v) => { setViewMode(v); setSearchQuery(''); setSearchIndex(0); }}
                  style={{ width: 90 }}
                  options={[
                    { value: 'list', label: 'List' },
                    { value: 'graph', label: 'Graph' },
                  ]}
                />
                <Button icon={<CodeSandboxOutlined />} onClick={() => setVarsOpen(true)}>
                  Variables
                </Button>
                {editor.flow?.status === 'published' ? (
                  <Popconfirm
                    title="Unpublish this flow?"
                    description="The bot will stop responding to this flow's triggers until it is republished."
                    okText="Unpublish"
                    okButtonProps={{ danger: true }}
                    cancelText="Cancel"
                    onConfirm={handleUnpublish}
                  >
                    <Button
                      danger
                      icon={<StopOutlined />}
                      loading={publishLoading}
                    >
                      Unpublish
                    </Button>
                  </Popconfirm>
                ) : (
                  <Button type="primary" icon={<SendOutlined />} onClick={handlePublish}>
                    Publish
                  </Button>
                )}
                <Button loading={editor.saving} onClick={() => editor.saveDraft(true)}>
                  Save
                </Button>
                <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddNodeOpen(true)}>
                  Add step
                </Button>
              </Space>
            </div>

            {/* View Body */}
            {viewMode === 'list' ? (
              <WorkflowListView
                definition={editor.definition}
                selectedNodeId={editor.selectedNodeId}
                onSelectNode={editor.selectNode}
                onDeleteNode={editor.deleteNode}
                onDisconnectEdge={editor.disconnectEdge}
                onInsertAfter={handleInsertAfter}
                onMoveNode={editor.moveNode}
                searchQuery={searchQuery}
                searchIndex={searchIndex}
                searchMatches={searchMatches}
              />
            ) : (
              <FlowCanvas
                definition={editor.definition}
                selectedNodeId={editor.selectedNodeId}
                onSelectNode={editor.selectNode}
                onConnect={editor.connectNodes}
              />
            )}
          </div>

          <NodeConfigDrawer
            open={editor.drawerOpen}
            node={editor.selectedNode}
            nodeOptions={editor.nodeOptions}
            placeholders={flowVars.placeholders}
            varOptions={flowVars.varOptions}
            definition={editor.definition}
            currentFlowId={flowId ? Number(flowId) : null}
            onClose={() => editor.setDrawerOpen(false)}
            onConfigChange={handleNodeConfigChange}
            onSaveNode={onSaveNode}
            onChangeNodeType={editor.changeNodeType}
          />
        </div>

        <FlowEditorFooter
          warningCount={editor.validation.warnings?.length || 0}
          lastSavedAt={editor.lastSavedAt}
          onUndo={editor.undo}
          onRedo={editor.redo}
          showZoom={viewMode === 'graph'}
          validation={editor.validation}
          onSelectNode={editor.selectNode}
        />

        <AddNodeDrawer
          open={addNodeOpen}
          onClose={() => setAddNodeOpen(false)}
          onSelect={handleAddNodeType}
          hasTrigger={editor.definition.nodes?.some((n) => n.type.startsWith('trigger.'))}
        />

        <Drawer
          title="Variables & Placeholders"
          placement="right"
          width={780}
          open={varsOpen}
          onClose={() => setVarsOpen(false)}
          styles={{ body: { padding: 20, background: '#f8fafc' } }}
        >
          <VariablesGuide definition={editor.definition} />
        </Drawer>

        <PublishModal
          open={publishOpen}
          onCancel={() => setPublishOpen(false)}
          onConfirm={confirmPublish}
          validation={editor.validation}
          loading={publishLoading}
        />

      </div>
    </FlowEditorContext.Provider>
  );
};

export default FlowEditor;
